
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";

import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";

import { globalStore } from "@/store/store";
import {
  findColorByVariableName,
  getGlobalColorCode,
  isCSSVariable,
  isValidHex
} from "@/lib/colors/colors";
import {
  Color,
  ColorInputValue,
  ColorV,
  GlobalColor,
  GradientColor,
  SolidColor,
  ColorOptions
} from "@/types/colors";

import ColorSwatch from "@/components/Reusable/CustomInputs/Color/ColorSwatch.vue";
import ColorPicker from "@/components/Reusable/CustomInputs/Color/ColorPicker.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import MediaChooserLayer from "@/components/Reusable/CustomInputs/MediaInput/MediaChooserLayer.vue";
import lodash from "lodash";

const DEFAULT_SOLID: SolidColor = {
  value: "",
  type: "solid"
};

const DEFAULT_GRADIENT: GradientColor = {
  value: "",
  svgValue: "",
  type: "linear",
  degree: 0,
  points: [
    {
      left: 0,
      red: 0,
      green: 0,
      blue: 0,
      alpha: 1
    },
    {
      left: 100,
      red: 255,
      green: 0,
      blue: 0,
      alpha: 1
    }
  ]
};

@Component({
  components: {
    MediaChooserLayer,
    TextInput,
    Dropdown,
    ColorPicker,
    ColorSwatch
  }
})
export default class ColorInput extends Vue {
  @Prop()
  value!: ColorInputValue;

  @Prop()
  colorOptions!: ColorOptions;

  @Prop({ default: true })
  showGlobal!: boolean;

  @Prop({ default: true })
  showInput!: boolean;

  @Prop({ default: false })
  small!: boolean;

  @Prop({ default: "right" })
  position!: "left" | "right";

  dialogOpen = false;

  get availableType() {
    return this.colorOptions?.type || "solidOnly";
  }

  get internalValue() {
    const maybeSolidColor: SolidColor | null =
      typeof this.value === "string"
        ? {
            value: this.value,
            type: "solid"
          }
        : null;

    const value = maybeSolidColor || this.value;
    const defaultColor =
      this.availableType === "gradientOnly" ? DEFAULT_GRADIENT : DEFAULT_SOLID;

    return pipe(
      value,
      ColorV.decode,
      fold(
        () => defaultColor,
        value => value
      )
    );
  }

  get colorCode() {
    if (this.globalColor) {
      return getGlobalColorCode(this.globalColor);
    }

    return this.internalValue.value;
  }

  get defaultColors() {
    const defaultColors = [
      {
        label: "Brand",
        path: "settings.colors.brand",
        cssVariableName: "bde-brand-primary-color"
      },
      {
        label: "Text",
        path: "settings.colors.text",
        cssVariableName: "bde-body-text-color"
      },
      {
        label: "Headings",
        path: "settings.colors.headings",
        cssVariableName: "bde-headings-color"
      },
      {
        label: "Links",
        path: "settings.colors.links",
        cssVariableName: "bde-links-color"
      },
      {
        label: "Background",
        path: "settings.colors.background",
        cssVariableName: "bde-background-color"
      }
    ];

    return defaultColors
      .map(color => {
        return {
          label: color.label,
          cssVariableName: color.cssVariableName,
          value: lodash.get(globalStore.globalSettings, color.path)
        };
      })
      .filter(c => c.value);
  }

  get globalColor(): GlobalColor | undefined {
    return findColorByVariableName(this.internalValue, this.globalColors);
  }

  get globalColors(): GlobalColor[] {
    return [
      ...this.defaultColors,
      ...globalStore.globalColors,
      ...globalStore.globalGradients
    ];
  }

  get isCSSVariable() {
    if (!this.internalValue) return false;
    return isCSSVariable(this.internalValue);
  }

  get canSaveColor() {
    return !this.isCSSVariable && this.value;
  }

  get isLarge() {
    return this.availableType === "gradientOnly" && !this.small;
  }

  onInput(color: string | null) {
    if (!color) {
      this.$emit("input", null);
      return;
    }

    const isValid = isValidHex(color) && !color.includes("#");
    const hexColor = isValid ? `#${color}` : color;

    this.$emit("input", hexColor);
  }

  // Color if gradient is enabled, otherwise string
  updateValue(newColor: Color | string) {
    if (!newColor) {
      this.$emit("input", null);
      return;
    }

    this.$emit("input", newColor);
  }
}
